import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Package, Truck, CheckCircle2, Clock, ChevronRight, Calendar, ChevronDown, ChevronUp, ArrowLeft, Armchair, MapPin, Settings2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import CRRReportPanel from './CRRReportPanel';
import KPICard from './KPICard';
import DeliveryReport from './DeliveryReport';

const API = process.env.REACT_APP_BACKEND_URL;

/* ================================================================
   COACH / SET REPORT
   ================================================================ */
function CoachReport({ category, onNavigateToPlanning, onProjectChange }) {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  // Notify parent whenever the selected project changes so it can hide
  // City-Bus-wide widgets (e.g. Dock Date Schedule) while drilling in.
  useEffect(() => {
    if (onProjectChange) onProjectChange(selectedProjectId);
  }, [selectedProjectId, onProjectChange]);
  const [derivedStatus, setDerivedStatus] = useState(null);
  const [shipmentData, setShipmentData] = useState(null);
  const [shipGroupBy, setShipGroupBy] = useState('week');
  const [expandedProject, setExpandedProject] = useState(null);
  const [expandedShipPeriods, setExpandedShipPeriods] = useState(new Set());
  const [showCompletedSets, setShowCompletedSets] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/planning/report/set-projects?category=${category}`);
      setReportData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [category]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // Fetch seat placed shipment data
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/planning/report/shipment-seat-placed?category=${category}&group_by=${shipGroupBy}`);
        setShipmentData(res.data);
      } catch (err) { console.error(err); }
    })();
  }, [category, shipGroupBy]);

  useEffect(() => {
    if (!selectedProjectId) { setDerivedStatus(null); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/api/planning/projects/${selectedProjectId}/set-derived-status`);
        setDerivedStatus(res.data);
      } catch { }
    })();
  }, [selectedProjectId]);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full" /></div>;
  if (!reportData) return null;

  const t = reportData.totals;
  const projects = reportData.projects || [];
  const selectedProject = selectedProjectId ? projects.find(p => p.id === selectedProjectId) : null;

  const CoachTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white rounded-lg shadow-xl border border-slate-200 p-3 text-xs">
        <div className="font-semibold text-slate-700 mb-1">{label}</div>
        {payload.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span className="text-slate-500">{p.name}:</span>
            <span className="font-semibold text-slate-700">{p.value?.toLocaleString()}</span>
          </div>
        ))}
      </div>
    );
  };

  const chartData = projects.map(p => ({
    name: p.sr_number ? `${p.name} (${p.sr_number})` : p.name, Ordered: p.ordered, Planned: p.planned, Completed: p.completed, Shipped: p.shipped, Shortage: p.shortage,
  }));

  return (
    <div className="space-y-4">
      {selectedProject ? (
        /* PROJECT DETAIL VIEW */
        <div className="space-y-4">
          <button onClick={() => setSelectedProjectId(null)}
            className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
            data-testid="back-to-overview">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Overview
          </button>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <h2 className="text-lg font-bold text-slate-800">{selectedProject.name}</h2>
              <span className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-semibold border border-orange-200">{selectedProject.category}</span>
              {selectedProject.sr_number && <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{selectedProject.sr_number}</span>}
              {selectedProject.seat_type_name && <span className="text-[10px] bg-violet-50 text-violet-600 px-2 py-0.5 rounded-full font-semibold border border-violet-200">{selectedProject.seat_type_name}</span>}
              {selectedProject.has_nemo && selectedProject.nemo_seat_type && <span className="text-[10px] bg-teal-50 text-teal-600 px-2 py-0.5 rounded-full font-semibold border border-teal-200">{selectedProject.nemo_seat_type}</span>}
            </div>
            <div className="flex items-center gap-4 text-[11px] text-slate-500 flex-wrap">
              {selectedProject.customer_name && <span>Customer: <strong className="text-slate-700">{selectedProject.customer_name}</strong></span>}
              <span>Seats/Set: <strong className="text-slate-700">{selectedProject.total_seats_per_set}</strong>
                {selectedProject.seat_type_name && ` (${selectedProject.seat_type_name}: ${selectedProject.units_per_set}`}
                {selectedProject.has_nemo && ` + ${selectedProject.nemo_seat_type}: ${selectedProject.nemo_seats_per_set}`}
                {selectedProject.seat_type_name && ')'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <KPICard label="Ordered" value={selectedProject.ordered} icon={<Package className="w-5 h-5" />} color="violet" badge="sets"
              subtitle={`${(selectedProject.ordered * (selectedProject.total_seats_per_set || 0)).toLocaleString()} seat placed`} />
            <KPICard label="Planned" value={selectedProject.planned} icon={<Calendar className="w-5 h-5" />} color="amber" badge="sets"
              subtitle={`${(selectedProject.planned * (selectedProject.total_seats_per_set || 0)).toLocaleString()} seat placed`} />
            <KPICard label="Completed" value={selectedProject.completed} icon={<CheckCircle2 className="w-5 h-5" />} color="emerald" badge="sets"
              subtitle={`${(selectedProject.completed * (selectedProject.total_seats_per_set || 0)).toLocaleString()} seat placed`} />
            <KPICard label="Shipped" value={selectedProject.shipped} icon={<Truck className="w-5 h-5" />} color="blue" badge="sets"
              subtitle={`${(selectedProject.shipped * (selectedProject.total_seats_per_set || 0)).toLocaleString()} seat placed`} />
            <KPICard label="Backlog" value={selectedProject.shortage} icon={<AlertTriangle className="w-5 h-5" />} color={selectedProject.shortage > 0 ? 'red' : 'emerald'} badge="sets"
              subtitle={`${(selectedProject.shortage * (selectedProject.total_seats_per_set || 0)).toLocaleString()} seat placed`} />
          </div>

          {/* Component Breakdown for projects with component data */}
          {selectedProject.component_breakdown && selectedProject.component_breakdown.length > 0 && (
            <div className="bg-white border border-indigo-200 rounded-xl overflow-hidden" data-testid="component-breakdown-detail">
              <div className="bg-indigo-50 px-4 py-2.5 border-b border-indigo-200 flex items-center gap-2">
                <Settings2 className="w-3.5 h-3.5 text-indigo-600" />
                <span className="text-xs font-bold text-indigo-800">Component Breakdown</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3">
                {selectedProject.component_breakdown.map(cb => {
                  const pct = cb.needed > 0 ? Math.round((cb.done / cb.needed) * 100) : 0;
                  return (
                    <div key={cb.name} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-slate-700">{cb.name}</span>
                        <span className="text-[9px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-semibold">{cb.qty_per_set}/set</span>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-center mb-2">
                        <div>
                          <div className="text-[9px] text-slate-400 font-medium">Needed</div>
                          <div className="text-sm font-bold text-violet-600">{cb.needed}</div>
                        </div>
                        <div>
                          <div className="text-[9px] text-slate-400 font-medium">Done</div>
                          <div className="text-sm font-bold text-emerald-600">{cb.done}</div>
                        </div>
                        <div>
                          <div className="text-[9px] text-slate-400 font-medium">Shipped</div>
                          <div className="text-sm font-bold text-blue-600">{cb.shipped}</div>
                        </div>
                        <div>
                          <div className="text-[9px] text-slate-400 font-medium">Remaining</div>
                          <div className={`text-sm font-bold ${cb.remaining > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{cb.remaining}</div>
                        </div>
                      </div>
                      <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                          style={{ width: `${Math.min(pct, 100)}%` }} />
                      </div>
                      <div className="text-right text-[9px] font-bold text-slate-500 mt-0.5">{pct}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Per-Set Component Detail */}
          {selectedProject.tracked_set_details && selectedProject.tracked_set_details.length > 0 && (
            <div className="bg-white border border-indigo-200 rounded-xl overflow-hidden" data-testid="tracked-set-detail-table">
              <div className="bg-indigo-50 px-4 py-2.5 border-b border-indigo-200 flex items-center gap-2">
                <Settings2 className="w-3.5 h-3.5 text-indigo-600" />
                <span className="text-xs font-bold text-indigo-800">Per-Set Component Status</span>
                <span className="text-[10px] text-indigo-400">({selectedProject.tracked_set_details.length} tracked sets)</span>
              </div>
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-indigo-50/50 text-[9px] uppercase font-bold text-indigo-600">
                    <th className="px-3 py-1.5 text-left">Set #</th>
                    <th className="px-3 py-1.5 text-left">Date</th>
                    <th className="px-3 py-1.5 text-left">Line</th>
                    <th className="px-3 py-1.5 text-center">Std Seat</th>
                    <th className="px-3 py-1.5 text-center">Flip-Up</th>
                    <th className="px-3 py-1.5 text-center">Barrier</th>
                    <th className="px-3 py-1.5 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedProject.tracked_set_details.map(s => {
                    const compBadge = (c) => {
                      if (c.rescheduled_to) return <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-purple-100 text-purple-600" title={`Moved to ${c.rescheduled_to}`}>{'\u2192'} {c.rescheduled_to}</span>;
                      if (c.carried_over) return <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-emerald-100 text-emerald-700" title="Produced on earlier date">Done</span>;
                      if (c.shipped) return <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-violet-100 text-violet-700">Shipped</span>;
                      if (c.done) return <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-emerald-100 text-emerald-700">Done</span>;
                      return <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-red-100 text-red-600">Missing</span>;
                    };
                    const activeComps = ['std_seat', 'flip_up', 'barrier'].filter(ck => !s[ck].carried_over && !s[ck].rescheduled_to);
                    const allDone = activeComps.length > 0 && activeComps.every(ck => s[ck].shipped);
                    const hasIssue = activeComps.some(ck => !s[ck].done);
                    return (
                      <tr key={`${s.set_number}-${s.date}`} className={`border-t border-slate-100 ${allDone ? 'bg-emerald-50/20' : hasIssue ? 'bg-red-50/30' : ''}`}>
                        <td className="px-3 py-2 font-bold text-slate-700">Set {s.set_number}</td>
                        <td className="px-3 py-2 text-[10px]">
                          {onNavigateToPlanning ? (
                            <button onClick={() => onNavigateToPlanning(s.date)}
                              className="text-blue-600 font-semibold hover:text-blue-800 hover:underline cursor-pointer">
                              {s.date || '-'}
                            </button>
                          ) : (
                            <span className="text-blue-600 font-semibold">{s.date || '-'}</span>
                          )}
                          {s.rescheduled_from && <span className="text-[8px] text-purple-400 ml-1">from {s.rescheduled_from}</span>}
                        </td>
                        <td className="px-3 py-2 text-slate-500 text-[10px]">{s.line_name || '-'}</td>
                        <td className="px-3 py-2 text-center">{compBadge(s.std_seat)}</td>
                        <td className="px-3 py-2 text-center">{compBadge(s.flip_up)}</td>
                        <td className="px-3 py-2 text-center">{compBadge(s.barrier)}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${allDone ? 'bg-emerald-100 text-emerald-700' : hasIssue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                            {allDone ? 'Complete' : hasIssue ? 'Incomplete' : 'Partial'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {derivedStatus && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200 flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-orange-500" />
                <span className="text-xs font-bold text-slate-700">Set Tracking (from Production Plans)</span>
                <span className="text-[10px] text-slate-400">({derivedStatus.totals?.ordered || 0} sets)</span>
              </div>
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-slate-50/50 text-[9px] text-slate-400 uppercase tracking-wider">
                    <th className="px-3 py-1.5 text-left w-14">Set #</th>
                    <th className="px-3 py-1.5 text-left">Dock Date</th>
                    <th className="px-3 py-1.5 text-left">Ship Date</th>
                    <th className="px-3 py-1.5 text-center">Planned</th>
                    <th className="px-3 py-1.5 text-center">Produced</th>
                    <th className="px-3 py-1.5 text-center">Shipped</th>
                    <th className="px-3 py-1.5 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {derivedStatus.schedules.map((s, i) => {
                    const statusMap = {
                      shipped: { label: s.days_late > 0 ? `Shipped (${s.days_late}d late)` : 'Shipped', cls: s.days_late > 0 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-600' },
                      produced: { label: 'Produced', cls: 'bg-blue-100 text-blue-600' },
                      planned: { label: 'Planned', cls: 'bg-blue-50 text-blue-600' },
                      pending: { label: 'Pending', cls: 'bg-slate-100 text-slate-400' },
                    };
                    const st = statusMap[s.status] || statusMap.pending;
                    return (
                      <tr key={`${s.set_number}-${s.planned_date || i}`} className={`border-t border-slate-100 ${s.status === 'shipped' ? 'bg-emerald-50/20' : ''}`}>
                        <td className="px-3 py-2 font-bold text-slate-700">Set {s.set_number}</td>
                        <td className="px-3 py-2 text-slate-600">{s.line_entry_date || '-'}</td>
                        <td className="px-3 py-2 font-medium text-slate-600">{s.shipment_date || '-'}</td>
                        <td className="px-3 py-2 text-center">{s.planned_date ? <span className="text-blue-600 font-medium">{s.planned_date}</span> : '-'}</td>
                        <td className="px-3 py-2 text-center">{s.produced_date ? <span className="text-emerald-600 font-medium">{s.produced_date}</span> : '-'}</td>
                        <td className="px-3 py-2 text-center">{s.shipped_date ? <span className="text-violet-600 font-medium">{s.shipped_date}</span> : '-'}</td>
                        <td className="px-3 py-2 text-center"><span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${st.cls}`}>{st.label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* CRR - Material Availability Report */}
          <CRRReportPanel projectId={selectedProjectId} />
        </div>
      ) : (
        /* OVERVIEW */
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="coach-kpi-cards">
            <KPICard label="Projects" value={t.total_projects} icon={<Package className="w-5 h-5" />} color="slate" badge={category} />
            <KPICard label="Total Orders" value={t.total_ordered} icon={<Armchair className="w-5 h-5" />} color="violet" badge="sets"
              subtitle={`${(t.total_ordered_sp || 0).toLocaleString()} seat placed`} />
            <KPICard label="Planned" value={t.total_planned} icon={<Calendar className="w-5 h-5" />} color="amber" badge="sets"
              subtitle={`${(t.total_planned_sp || 0).toLocaleString()} seat placed`} />
            <KPICard label="Completed" value={t.total_completed} icon={<CheckCircle2 className="w-5 h-5" />} color="emerald" badge="sets"
              subtitle={`${(t.total_completed_sp || 0).toLocaleString()} seat placed`} />
            <KPICard label="Shipped" value={t.total_shipped} icon={<Truck className="w-5 h-5" />} color="blue" badge="sets"
              subtitle={`${(t.total_shipped_sp || 0).toLocaleString()} seat placed`} />
            <KPICard label="Backlog" value={t.total_shortage} icon={<AlertTriangle className="w-5 h-5" />} color={t.total_shortage > 0 ? 'red' : 'emerald'} badge="sets"
              subtitle={`${(t.total_shortage_sp || 0).toLocaleString()} seat placed`} />
          </div>

          {/* Global Component Shortage Summary */}
          {t.comp_shortage && Object.keys(t.comp_shortage).filter(k => k !== 'std_seat').length > 0 && (
            <div className="bg-white border border-amber-200 rounded-xl overflow-hidden" data-testid="global-comp-shortage">
              <div className="bg-amber-50 px-4 py-2.5 border-b border-amber-200 flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                <span className="text-xs font-bold text-amber-800">Component Backlog (All Tracked Sets)</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3">
                {Object.entries(t.comp_shortage).map(([key, cs]) => {
                  const pct = cs.needed > 0 ? Math.round((cs.done / cs.needed) * 100) : 0;
                  return (
                    <div key={key} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-slate-700">{cs.name}</span>
                        <span className="text-[9px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-semibold">{cs.tracked_sets} tracked</span>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-center mb-2">
                        <div>
                          <div className="text-[9px] text-slate-400 font-medium">Needed</div>
                          <div className="text-sm font-bold text-violet-600">{cs.needed}</div>
                        </div>
                        <div>
                          <div className="text-[9px] text-slate-400 font-medium">Done</div>
                          <div className="text-sm font-bold text-emerald-600">{cs.done}</div>
                        </div>
                        <div>
                          <div className="text-[9px] text-slate-400 font-medium">Shipped</div>
                          <div className="text-sm font-bold text-blue-600">{cs.shipped}</div>
                        </div>
                        <div>
                          <div className="text-[9px] text-slate-400 font-medium">Remaining</div>
                          <div className={`text-sm font-bold ${cs.remaining > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{cs.remaining}</div>
                        </div>
                      </div>
                      <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                          style={{ width: `${Math.min(pct, 100)}%` }} />
                      </div>
                      <div className="text-right text-[9px] font-bold text-slate-500 mt-0.5">{pct}%</div>
                    </div>
                  );
                })}
              </div>
              {/* Per-set detail table in global shortage */}
              {(() => {
                const allTracked = projects.flatMap(p =>
                  (p.tracked_set_details || []).map(s => ({ ...s, project: p.name, sr_number: p.sr_number || '' }))
                );
                const compIsMissing = (c) => !c.carried_over && !c.rescheduled_to && !c.shipped && !c.done;
                const incomplete = allTracked.filter(s => {
                  const comps = ['std_seat', 'flip_up', 'barrier'];
                  return comps.some(ck => compIsMissing(s[ck]));
                });
                const completed = allTracked.filter(s => {
                  const comps = ['std_seat', 'flip_up', 'barrier'];
                  const hasMissing = comps.some(ck => compIsMissing(s[ck]));
                  if (hasMissing) return false;
                  return comps.some(ck => {
                    const c = s[ck];
                    return (c.done && !c.shipped) || c.rescheduled_to;
                  });
                });
                if (incomplete.length === 0 && completed.length === 0) return null;
                const compBadge = (c) => {
                  if (c.rescheduled_to) return <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-purple-100 text-purple-600" title={`Moved to ${c.rescheduled_to}`}>{'\u2192'} {c.rescheduled_to}</span>;
                  if (c.carried_over) return <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-emerald-100 text-emerald-700" title="Produced on earlier date">Done</span>;
                  if (c.shipped) return <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-violet-100 text-violet-700">Shipped</span>;
                  if (c.done) return <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-emerald-100 text-emerald-700">Done</span>;
                  return <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-red-100 text-red-600">Missing</span>;
                };
                const renderSetTable = (sets, headerBg, headerText, rowHover) => (
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className={`${headerBg} text-[9px] uppercase font-bold ${headerText}`}>
                        <th className="px-3 py-1.5 text-left">Project</th>
                        <th className="px-3 py-1.5 text-left">Program #</th>
                        <th className="px-3 py-1.5 text-left">Set #</th>
                        <th className="px-3 py-1.5 text-left">Date</th>
                        <th className="px-3 py-1.5 text-left">Line</th>
                        <th className="px-3 py-1.5 text-center">Std Seat</th>
                        <th className="px-3 py-1.5 text-center">Flip-Up</th>
                        <th className="px-3 py-1.5 text-center">Barrier</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sets.map((s, i) => (
                        <tr key={i} className={`border-t border-slate-100 ${rowHover}`}>
                          <td className="px-3 py-1.5 font-medium text-slate-600">{s.project}</td>
                          <td className="px-3 py-1.5 text-slate-500 font-medium">{s.sr_number || '-'}</td>
                          <td className="px-3 py-1.5 font-bold text-slate-700">Set {s.set_number}</td>
                          <td className="px-3 py-1.5">
                            {onNavigateToPlanning ? (
                              <button onClick={() => onNavigateToPlanning(s.date)}
                                className="text-blue-600 font-semibold hover:text-blue-800 hover:underline cursor-pointer"
                                title="Go to Planning for this date"
                                data-testid={`navigate-date-${s.set_number}-${s.date}`}>
                                {s.date || '-'}
                              </button>
                            ) : (
                              <span className="text-blue-600 font-semibold">{s.date || '-'}</span>
                            )}
                            {s.rescheduled_from && <div className="text-[8px] text-purple-400">from {s.rescheduled_from}</div>}
                          </td>
                          <td className="px-3 py-1.5 text-slate-500">{s.line_name || '-'}</td>
                          <td className="px-3 py-1.5 text-center">{compBadge(s.std_seat)}</td>
                          <td className="px-3 py-1.5 text-center">{compBadge(s.flip_up)}</td>
                          <td className="px-3 py-1.5 text-center">{compBadge(s.barrier)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
                return (
                  <div className="border-t border-amber-200">
                    {incomplete.length > 0 && (
                      <>
                        <div className="px-4 py-2 bg-amber-50/50 flex items-center gap-2">
                          <span className="text-[10px] font-bold text-amber-700" data-testid="incomplete-sets-title">Incomplete Sets Detail</span>
                          <span className="text-[9px] text-amber-500">({incomplete.length} sets with missing components)</span>
                        </div>
                        {renderSetTable(incomplete, 'bg-amber-50/30', 'text-amber-600', 'hover:bg-blue-50/30')}
                      </>
                    )}
                    {completed.length > 0 && (
                      <div className="border-t border-emerald-200">
                        <div className="px-4 py-2 bg-emerald-50/50 flex items-center gap-2 cursor-pointer select-none"
                          onClick={() => setShowCompletedSets(!showCompletedSets)}
                          data-testid="toggle-completed-sets">
                          <span className="text-[10px] font-bold text-emerald-700">Completed Sets</span>
                          <span className="text-[9px] text-emerald-500">({completed.length} sets - all components done)</span>
                          <span className="ml-auto text-emerald-500">
                            {showCompletedSets ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                          </span>
                        </div>
                        {showCompletedSets && renderSetTable(completed, 'bg-emerald-50/30', 'text-emerald-600', 'hover:bg-emerald-50/30')}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ─── NEMO BREAKDOWN ─── */}
          {(t.nemo_projects > 0 || projects.some(p => p.has_nemo || p.is_nemo_project)) && (() => {
            const nemoProjects = projects.filter(p => p.has_nemo || p.is_nemo_project);
            if (nemoProjects.length === 0) return null;
            return (
              <div className="bg-white border border-violet-200 rounded-xl overflow-hidden" data-testid="nemo-breakdown">
                <div className="bg-violet-50 px-4 py-2.5 border-b border-violet-200 flex items-center gap-2">
                  <Armchair className="w-3.5 h-3.5 text-violet-600" />
                  <span className="text-xs font-bold text-violet-800">Nemo Seat Breakdown</span>
                  <span className="text-[9px] bg-violet-200 text-violet-700 px-1.5 py-0.5 rounded-full font-semibold ml-1">{nemoProjects.length} projects</span>
                </div>
                {/* Nemo Summary KPIs */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 p-3 border-b border-violet-100 bg-violet-50/30">
                  <div className="text-center">
                    <div className="text-[10px] text-violet-500 font-medium">Ordered</div>
                    <div className="text-lg font-bold text-violet-700">{(t.nemo_ordered_sp || 0).toLocaleString()}</div>
                    <div className="text-[9px] text-violet-400">{t.nemo_ordered_sets || 0} sets</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] text-emerald-500 font-medium">Completed</div>
                    <div className="text-lg font-bold text-emerald-600">{(t.nemo_completed_sp || 0).toLocaleString()}</div>
                    <div className="text-[9px] text-emerald-400">{t.nemo_completed_sets || 0} sets</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] text-teal-500 font-medium">Shipped</div>
                    <div className="text-lg font-bold text-teal-600">{(t.nemo_shipped_sp || 0).toLocaleString()}</div>
                    <div className="text-[9px] text-teal-400">{t.nemo_shipped_sets || 0} sets</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] text-red-500 font-medium">Remaining</div>
                    <div className={`text-lg font-bold ${(t.nemo_remaining_sp || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{(t.nemo_remaining_sp || 0).toLocaleString()}</div>
                    <div className="text-[9px] text-red-400">{t.nemo_remaining_sets || 0} sets</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] text-blue-500 font-medium">Main Seats</div>
                    <div className="text-lg font-bold text-blue-600">{(t.main_shipped_sp || 0).toLocaleString()}</div>
                    <div className="text-[9px] text-blue-400">shipped</div>
                  </div>
                </div>
                {/* Per-project Nemo detail */}
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="bg-violet-50/50 text-[9px] text-violet-400 uppercase tracking-wider">
                      <th className="px-3 py-1.5 text-left">Project</th>
                      <th className="px-3 py-1.5 text-left">Nemo Type</th>
                      <th className="px-3 py-1.5 text-center">Nemo/Set</th>
                      <th className="px-3 py-1.5 text-center">Ordered (set)</th>
                      <th className="px-3 py-1.5 text-center text-teal-500">Shipped Nemo</th>
                      <th className="px-3 py-1.5 text-center text-red-500">Remaining Nemo</th>
                      <th className="px-3 py-1.5 text-center">Main Seat Type</th>
                      <th className="px-3 py-1.5 text-center text-blue-500">Main Shipped</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nemoProjects.map(p => {
                      const nemoPerSet = p.is_nemo_project ? (p.units_per_set + (p.nemo_seats_per_set || 0)) : (p.nemo_seats_per_set || 0);
                      const mainPerSet = p.is_nemo_project ? 0 : p.units_per_set;
                      const nemoType = p.is_nemo_project ? p.seat_type_name : (p.nemo_seat_type || 'Nemo');
                      const mainType = p.is_nemo_project ? '-' : p.seat_type_name;
                      const shortage = Math.max(0, p.ordered - p.shipped);
                      return (
                        <tr key={p.id} className="border-t border-violet-100 hover:bg-violet-50/20">
                          <td className="px-3 py-2">
                            <div className="font-bold text-slate-700">{p.name}</div>
                            {p.sr_number && <div className="text-[9px] text-slate-400">{p.sr_number}</div>}
                          </td>
                          <td className="px-3 py-2 text-violet-600 font-semibold">{nemoType}</td>
                          <td className="px-3 py-2 text-center font-bold text-violet-700">{nemoPerSet}</td>
                          <td className="px-3 py-2 text-center font-medium text-slate-600">{p.ordered}</td>
                          <td className="px-3 py-2 text-center font-bold text-teal-600">{(p.shipped * nemoPerSet).toLocaleString()}</td>
                          <td className={`px-3 py-2 text-center font-bold ${shortage > 0 ? 'text-red-600 bg-red-50/30' : 'text-emerald-600'}`}>
                            {(shortage * nemoPerSet).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-center text-blue-600 font-medium">{mainType} {mainPerSet > 0 ? `(${mainPerSet})` : ''}</td>
                          <td className="px-3 py-2 text-center font-bold text-blue-600">{mainPerSet > 0 ? (p.shipped * mainPerSet).toLocaleString() : '-'}</td>
                        </tr>
                      );
                    })}
                    {/* Totals */}
                    <tr className="border-t-2 border-violet-300 bg-violet-50 font-bold text-xs">
                      <td className="px-3 py-2 text-violet-800" colSpan={2}>TOTAL NEMO</td>
                      <td className="px-3 py-2"></td>
                      <td className="px-3 py-2 text-center text-slate-600">{t.nemo_ordered_sets || 0}</td>
                      <td className="px-3 py-2 text-center text-teal-700">{(t.nemo_shipped_sp || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-center text-red-700 bg-red-50/30">{(t.nemo_remaining_sp || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-center text-blue-700">Main Total</td>
                      <td className="px-3 py-2 text-center text-blue-700">{(t.main_shipped_sp || 0).toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })()}

          {chartData.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-3">Project Comparison</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip content={<CoachTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Bar dataKey="Ordered" fill="#8b5cf6" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="Planned" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="Completed" fill="#10b981" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="Shipped" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="Shortage" fill="#ef4444" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200">
              <span className="text-xs font-bold text-slate-700">{category} Projects</span>
            </div>
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-slate-50/50 text-[9px] text-slate-400 uppercase tracking-wider">
                  <th className="px-3 py-1.5 text-left">Project</th>
                  <th className="px-3 py-1.5 text-left">Program #</th>
                  <th className="px-3 py-1.5 text-left">Seat Type</th>
                  <th className="px-3 py-1.5 text-center">Ordered</th>
                  <th className="px-3 py-1.5 text-center">Planned</th>
                  <th className="px-3 py-1.5 text-center">Completed</th>
                  <th className="px-3 py-1.5 text-center">Shipped</th>
                  <th className="px-3 py-1.5 text-center">Backlog</th>
                  <th className="px-3 py-1.5 text-center"></th>
                </tr>
              </thead>
              <tbody>
                {projects.map(p => (
                  <React.Fragment key={p.id}>
                    <tr className="border-t border-slate-100 hover:bg-orange-50/30 cursor-pointer" onClick={() => setSelectedProjectId(p.id)} data-testid={`project-row-${p.id}`}>
                      <td className="px-3 py-2.5">
                        <div className="font-bold text-slate-700">{p.name}</div>
                        <div className="text-[9px] text-slate-400">{p.customer_name || '-'}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        {p.sr_number ? <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">{p.sr_number}</span> : '-'}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-col gap-0.5">
                          {p.seat_type_name && <span className="text-[10px] text-violet-600 font-medium">{p.seat_type_name} ({p.units_per_set})</span>}
                          {p.has_nemo && p.nemo_seat_type && <span className="text-[10px] text-teal-600 font-medium">{p.nemo_seat_type} ({p.nemo_seats_per_set})</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-center font-bold text-violet-600">{p.ordered}</td>
                      <td className="px-3 py-2.5 text-center font-medium text-orange-600">{p.planned}</td>
                      <td className="px-3 py-2.5 text-center font-medium text-emerald-600">{p.completed}</td>
                      <td className="px-3 py-2.5 text-center font-medium text-blue-600">{p.shipped}</td>
                      <td className={`px-3 py-2.5 text-center font-bold ${p.shortage > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{p.shortage}</td>
                      <td className="px-3 py-2.5 text-center"><ChevronRight className="w-3.5 h-3.5 text-slate-300" /></td>
                    </tr>
                    {/* Component Breakdown Row */}
                    {p.component_breakdown && p.component_breakdown.length > 0 && (
                      <tr className="bg-indigo-50/30 border-t border-indigo-100">
                        <td colSpan={9} className="px-4 py-2">
                          <div className="flex items-center gap-6 flex-wrap">
                            {p.component_breakdown.map(cb => (
                              <div key={cb.name} className="flex items-center gap-2 bg-white rounded-lg px-3 py-1.5 border border-indigo-200 shadow-sm">
                                <span className="text-[10px] font-bold text-indigo-700">{cb.name}</span>
                                <span className="text-[9px] text-slate-400">({cb.qty_per_set}/set)</span>
                                <span className="text-[10px] text-violet-600 font-semibold">Need: {cb.needed}</span>
                                <span className="text-[10px] text-emerald-600 font-semibold">Done: {cb.done}</span>
                                <span className={`text-[10px] font-bold ${cb.remaining > 0 ? 'text-red-600' : 'text-emerald-600'}`}>Rem: {cb.remaining}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {projects.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">No {category} projects found</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ========= OPEN ORDERS CHART ========= */}
          {(() => {
            const openOrders = projects
              .filter(p => p.ordered > 0 && p.shipped < p.ordered)
              .map(p => {
                const totalSeatsPerSet = (p.units_per_set || 0) + (p.has_nemo ? (p.nemo_seats_per_set || 0) : 0);
                const remainingSets = Math.max(0, p.ordered - p.shipped);
                return {
                  name: p.name,
                  sr: p.sr_number || '',
                  seatType: p.seat_type_name || '',
                  ordered: p.ordered,
                  shipped: p.shipped,
                  remaining_sets: remainingSets,
                  remaining_sp: remainingSets * totalSeatsPerSet,
                  seats_per_set: totalSeatsPerSet,
                  pct: p.ordered > 0 ? Math.round((p.shipped / p.ordered) * 100) : 0,
                };
              });
            const totalRemainingSets = openOrders.reduce((s, o) => s + o.remaining_sets, 0);
            const totalRemainingSP = openOrders.reduce((s, o) => s + o.remaining_sp, 0);

            if (openOrders.length === 0) return null;
            return (
              <div className="space-y-4" data-testid="open-orders-section">
                {/* Open Orders KPIs */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <KPICard label="Open Orders" value={openOrders.length}
                    icon={<Package className="w-4 h-4" />} color="orange" badge="project" />
                  <KPICard label="Remaining Sets" value={totalRemainingSets}
                    icon={<Clock className="w-4 h-4" />} color="red" badge="set" />
                  <KPICard label="Remaining Seat Placed" value={totalRemainingSP}
                    icon={<Armchair className="w-4 h-4" />} color="red" badge="seat placed" />
                </div>

                {/* Open Orders Chart */}
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="mb-3">
                    <h3 className="text-sm font-bold text-slate-700">Open Orders Progress</h3>
                    <p className="text-[10px] text-slate-400">Shipped vs Remaining per project (set + seat placed)</p>
                  </div>
                  <ResponsiveContainer width="100%" height={Math.max(200, openOrders.length * 60)}>
                    <BarChart data={openOrders} layout="vertical" barGap={2}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                      <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10, fill: '#475569', fontWeight: 600 }} />
                      <Tooltip content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0]?.payload;
                        return (
                          <div className="bg-white rounded-lg shadow-xl border border-slate-200 p-3 text-xs space-y-1">
                            <div className="font-bold text-slate-800">{d?.name}</div>
                            {d?.sr && <div className="text-slate-400">{d.sr}</div>}
                            {d?.seatType && <div className="text-violet-500 font-medium">{d.seatType} ({d.seats_per_set} seats/set)</div>}
                            <div className="flex gap-3 pt-1 border-t border-slate-100">
                              <span className="text-teal-600">Shipped: <b>{d?.shipped} set</b></span>
                              <span className="text-red-600">Remaining: <b>{d?.remaining_sets} set</b></span>
                            </div>
                            <div className="text-red-600">Remaining SP: <b>{d?.remaining_sp?.toLocaleString()}</b></div>
                            <div className="text-slate-500">Progress: <b>{d?.pct}%</b></div>
                          </div>
                        );
                      }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Bar dataKey="shipped" name="Shipped (set)" stackId="a" fill="#0d9488" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="remaining_sets" name="Remaining (set)" stackId="a" fill="#ef4444" radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Open Orders Detail Table */}
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="open-orders-table">
                  <div className="bg-red-50 px-4 py-2.5 border-b border-red-200 flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                    <span className="text-xs font-bold text-red-800">Open Orders Detail</span>
                  </div>
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="bg-slate-50/50 text-[9px] text-slate-400 uppercase tracking-wider">
                        <th className="px-3 py-1.5 text-left">Project</th>
                        <th className="px-3 py-1.5 text-left">Program #</th>
                        <th className="px-3 py-1.5 text-left">Seat Type</th>
                        <th className="px-3 py-1.5 text-center">Seats/Set</th>
                        <th className="px-3 py-1.5 text-center">Ordered (set)</th>
                        <th className="px-3 py-1.5 text-center text-teal-600">Shipped (set)</th>
                        <th className="px-3 py-1.5 text-center text-red-600 font-bold">Remaining (set)</th>
                        <th className="px-3 py-1.5 text-center text-red-600 font-bold">Remaining (seat placed)</th>
                        <th className="px-3 py-1.5 text-center">Progress</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openOrders.map((o, i) => (
                        <tr key={i} className="border-t border-slate-100 hover:bg-red-50/20">
                          <td className="px-3 py-2 font-bold text-slate-700">{o.name}</td>
                          <td className="px-3 py-2 text-slate-500 font-medium">{o.sr || '-'}</td>
                          <td className="px-3 py-2 text-violet-600 font-medium">{o.seatType || '-'}</td>
                          <td className="px-3 py-2 text-center text-slate-600 font-semibold">{o.seats_per_set}</td>
                          <td className="px-3 py-2 text-center font-bold text-violet-600">{o.ordered}</td>
                          <td className="px-3 py-2 text-center font-bold text-teal-600">{o.shipped}</td>
                          <td className="px-3 py-2 text-center font-bold text-red-600">{o.remaining_sets}</td>
                          <td className="px-3 py-2 text-center font-bold text-red-700 bg-red-50/40">{o.remaining_sp.toLocaleString()}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              <div className="flex-1 bg-slate-200 rounded-full h-1.5 overflow-hidden">
                                <div className="h-full rounded-full transition-all" style={{
                                  width: `${o.pct}%`,
                                  backgroundColor: o.pct >= 80 ? '#0d9488' : o.pct >= 50 ? '#f59e0b' : '#ef4444'
                                }} />
                              </div>
                              <span className="text-[9px] font-bold text-slate-500 w-8 text-right">{o.pct}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold text-xs">
                        <td className="px-3 py-2 text-slate-700" colSpan={4}>TOTAL</td>
                        <td className="px-3 py-2 text-center text-violet-600">{openOrders.reduce((s, o) => s + o.ordered, 0)}</td>
                        <td className="px-3 py-2 text-center text-teal-600">{openOrders.reduce((s, o) => s + o.shipped, 0)}</td>
                        <td className="px-3 py-2 text-center text-red-600">{totalRemainingSets}</td>
                        <td className="px-3 py-2 text-center text-red-700 bg-red-50/40">{totalRemainingSP.toLocaleString()}</td>
                        <td className="px-3 py-2"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* ========= SEAT PLACED SHIPMENT REPORT ========= */}
          {shipmentData && (shipmentData.totals?.shipped_sets > 0 || shipmentData.totals?.completed_sets > 0) && (
            <div className="space-y-4" data-testid="seat-placed-report">
              {/* Seat Placed Summary KPIs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <KPICard label="Shipped Sets" value={shipmentData.totals.shipped_sets}
                  icon={<Truck className="w-4 h-4" />} color="teal" badge="set" />
                <KPICard label="Shipped Seat Placed" value={shipmentData.totals.shipped_seat_placed}
                  icon={<Armchair className="w-4 h-4" />} color="blue" badge="seat placed" />
                <KPICard label="Completed Sets" value={shipmentData.totals.completed_sets}
                  icon={<CheckCircle2 className="w-4 h-4" />} color="emerald" badge="set" />
                <KPICard label="Completed Seat Placed" value={shipmentData.totals.completed_seat_placed}
                  icon={<Armchair className="w-4 h-4" />} color="violet" badge="seat placed" />
              </div>

              {/* Per-Project Seat Placed Summary with Set Tracker */}
              {shipmentData.projects_summary?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="bg-teal-50 px-4 py-2.5 border-b border-teal-200 flex items-center gap-2">
                    <Armchair className="w-3.5 h-3.5 text-teal-600" />
                    <span className="text-xs font-bold text-teal-800">Seat Placed by Project</span>
                  </div>
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="bg-slate-50/50 text-[9px] text-slate-400 uppercase tracking-wider">
                        <th className="px-3 py-1.5 text-left">Project</th>
                        <th className="px-3 py-1.5 text-left">Seat Type</th>
                        <th className="px-3 py-1.5 text-center">Seats/Set</th>
                        <th className="px-3 py-1.5 text-center">Ordered</th>
                        <th className="px-3 py-1.5 text-center">Shipped (set)</th>
                        <th className="px-3 py-1.5 text-center font-bold text-teal-600">Shipped (seat placed)</th>
                        <th className="px-3 py-1.5 text-center text-red-500 font-bold">Remaining (set)</th>
                        <th className="px-3 py-1.5 text-center text-red-500 font-bold">Remaining (seat placed)</th>
                        <th className="px-3 py-1.5 text-center"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {shipmentData.projects_summary.map(ps => (
                        <React.Fragment key={ps.id}>
                          <tr className="border-t border-slate-100 hover:bg-teal-50/20 cursor-pointer"
                            onClick={() => setExpandedProject(expandedProject === ps.id ? null : ps.id)}
                            data-testid={`sp-project-row-${ps.id}`}>
                            <td className="px-3 py-2 font-bold text-slate-700">{ps.name}</td>
                            <td className="px-3 py-2 text-violet-600 font-medium">{ps.seat_type || '-'}</td>
                            <td className="px-3 py-2 text-center font-semibold text-slate-600">{ps.seats_per_set}</td>
                            <td className="px-3 py-2 text-center font-medium text-slate-500">{ps.ordered}</td>
                            <td className="px-3 py-2 text-center font-bold text-teal-600">{ps.shipped_sets}</td>
                            <td className="px-3 py-2 text-center font-bold text-teal-700 bg-teal-50/40">{ps.shipped_seat_placed.toLocaleString()}</td>
                            <td className={`px-3 py-2 text-center font-bold ${ps.remaining_sets > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{ps.remaining_sets}</td>
                            <td className={`px-3 py-2 text-center font-bold ${ps.remaining_seat_placed > 0 ? 'text-red-600 bg-red-50/30' : 'text-emerald-600 bg-emerald-50/30'}`}>{ps.remaining_seat_placed.toLocaleString()}</td>
                            <td className="px-3 py-2 text-center">
                              {ps.set_tracker?.length > 0 && (
                                expandedProject === ps.id
                                  ? <ChevronUp className="w-3.5 h-3.5 text-slate-400 inline" />
                                  : <ChevronDown className="w-3.5 h-3.5 text-slate-400 inline" />
                              )}
                            </td>
                          </tr>
                          {/* Expanded Set Tracker */}
                          {expandedProject === ps.id && ps.set_tracker?.length > 0 && (
                            <tr>
                              <td colSpan={9} className="p-0">
                                <div className="bg-slate-50 border-y border-slate-200 px-4 py-3">
                                  <div className="text-[10px] font-bold text-slate-500 uppercase mb-2 flex items-center gap-1.5">
                                    <Package className="w-3 h-3" /> Set Detail - {ps.name}
                                  </div>
                                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1.5">
                                    {ps.set_tracker.map((st, idx) => (
                                      <div key={idx}
                                        className={`rounded-lg px-2 py-1.5 text-[10px] border ${
                                          st.status === 'shipped' ? 'bg-teal-50 border-teal-300 text-teal-800' :
                                          st.status === 'completed' ? 'bg-emerald-50 border-emerald-300 text-emerald-800' :
                                          st.status === 'planned' ? 'bg-orange-50 border-orange-300 text-orange-800' :
                                          'bg-slate-100 border-slate-200 text-slate-500'
                                        }`}
                                        data-testid={`set-tracker-${ps.id}-${idx}`}>
                                        <div className="font-bold">{st.set_info || `#${idx + 1}`}</div>
                                        <div className="text-[9px] opacity-70">{st.date}</div>
                                        <div className="flex items-center gap-1 mt-0.5">
                                          {st.status === 'shipped' && <Truck className="w-2.5 h-2.5" />}
                                          {st.status === 'completed' && <CheckCircle2 className="w-2.5 h-2.5" />}
                                          {st.status === 'planned' && <Clock className="w-2.5 h-2.5" />}
                                          <span className="capitalize font-semibold">{st.status}</span>
                                          {st.shipped > 0 && <span className="ml-auto font-bold">{st.seat_placed_shipped}sp</span>}
                                          {st.shipped === 0 && st.completed > 0 && <span className="ml-auto font-bold">{st.seat_placed_completed}sp</span>}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  {/* Progress bar */}
                                  <div className="mt-2.5 flex items-center gap-2">
                                    <div className="flex-1 bg-slate-200 rounded-full h-2 overflow-hidden">
                                      <div className="h-full bg-teal-500 rounded-full transition-all"
                                        style={{ width: `${ps.ordered > 0 ? Math.round((ps.shipped_sets / ps.ordered) * 100) : 0}%` }} />
                                    </div>
                                    <span className="text-[10px] font-bold text-slate-600 whitespace-nowrap">
                                      {ps.shipped_sets}/{ps.ordered} shipped ({ps.ordered > 0 ? Math.round((ps.shipped_sets / ps.ordered) * 100) : 0}%)
                                    </span>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                      {/* Totals row */}
                      <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold text-xs">
                        <td className="px-3 py-2 text-slate-700" colSpan={2}>TOTAL</td>
                        <td className="px-3 py-2"></td>
                        <td className="px-3 py-2 text-center text-slate-500">{shipmentData.projects_summary.reduce((s, p) => s + p.ordered, 0)}</td>
                        <td className="px-3 py-2 text-center text-teal-700">{shipmentData.totals.shipped_sets}</td>
                        <td className="px-3 py-2 text-center text-teal-800 bg-teal-50/40">{shipmentData.totals.shipped_seat_placed.toLocaleString()}</td>
                        <td className="px-3 py-2 text-center text-red-600">{shipmentData.projects_summary.reduce((s, p) => s + (p.remaining_sets || 0), 0)}</td>
                        <td className="px-3 py-2 text-center text-red-700 bg-red-50/30">{shipmentData.projects_summary.reduce((s, p) => s + (p.remaining_seat_placed || 0), 0).toLocaleString()}</td>
                        <td className="px-3 py-2"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Shipment Chart by Period */}
              {shipmentData.periods?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div>
                      <h3 className="text-sm font-bold text-slate-700">Shipment Timeline (Seat Placed)</h3>
                      <p className="text-[10px] text-slate-400">Sets shipped and seat placed by {shipGroupBy}</p>
                    </div>
                    <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-testid="ship-group-toggle">
                      {['day', 'week', 'month', 'year'].map(g => (
                        <button key={g} onClick={() => setShipGroupBy(g)}
                          className={`px-2.5 py-1 text-[10px] font-semibold rounded-md transition-all ${shipGroupBy === g ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                          data-testid={`ship-group-${g}`}>
                          {g === 'day' ? 'Daily' : g === 'week' ? 'Weekly' : g === 'month' ? 'Monthly' : 'Yearly'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={shipmentData.periods} barGap={4}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#94a3b8' }} interval={0} angle={shipGroupBy === 'day' ? -45 : 0} textAnchor={shipGroupBy === 'day' ? 'end' : 'middle'} height={shipGroupBy === 'day' ? 60 : 30} />
                      <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
                      <Tooltip content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        return (
                          <div className="bg-white rounded-lg shadow-xl border border-slate-200 p-3 text-xs">
                            <div className="font-semibold text-slate-700 mb-1">{label}</div>
                            {payload.map((p, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                                <span className="text-slate-500">{p.name}:</span>
                                <span className="font-semibold text-slate-700">{p.value?.toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        );
                      }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Bar dataKey="shipped_sets" name="Shipped (set)" fill="#0d9488" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="shipped_seat_placed" name="Shipped (seat placed)" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Period Detail Table */}
              {shipmentData.periods?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="shipment-period-table">
                  <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Truck className="w-3.5 h-3.5 text-teal-600" />
                      <span className="text-xs font-bold text-slate-700">Shipment Detail</span>
                    </div>
                    <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-testid="ship-table-group-toggle">
                      {['day', 'week', 'month', 'year'].map(g => (
                        <button key={g} onClick={() => setShipGroupBy(g)}
                          className={`px-2.5 py-1 text-[10px] font-semibold rounded-md transition-all ${shipGroupBy === g ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                          data-testid={`ship-table-group-${g}`}>
                          {g === 'day' ? 'Daily' : g === 'week' ? 'Weekly' : g === 'month' ? 'Monthly' : 'Yearly'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="bg-slate-50/50 text-[9px] text-slate-400 uppercase tracking-wider">
                        <th className="px-3 py-1.5 text-left font-semibold w-5"></th>
                        <th className="px-3 py-1.5 text-left font-semibold">{shipGroupBy === 'day' ? 'Date' : shipGroupBy === 'week' ? 'Week' : shipGroupBy === 'month' ? 'Month' : 'Year'}</th>
                        <th className="px-3 py-1.5 text-center font-semibold">Shipped (set)</th>
                        <th className="px-3 py-1.5 text-center font-semibold text-teal-600">Shipped (seat placed)</th>
                        <th className="px-3 py-1.5 text-center font-semibold">Completed (set)</th>
                        <th className="px-3 py-1.5 text-center font-semibold text-violet-600">Completed (seat placed)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shipmentData.periods.map((p, i) => (
                        <React.Fragment key={p.key}>
                          <tr className={`border-t border-slate-100 hover:bg-teal-50/20 cursor-pointer ${i % 2 === 0 ? '' : 'bg-slate-50/30'} ${expandedShipPeriods.has(p.key) ? 'bg-teal-50/30' : ''}`}
                            onClick={() => setExpandedShipPeriods(prev => {
                              const next = new Set(prev);
                              if (next.has(p.key)) next.delete(p.key);
                              else next.add(p.key);
                              return next;
                            })}
                            data-testid={`shipment-period-${p.key}`}>
                            <td className="px-3 py-2 text-slate-400">
                              <ChevronRight className={`w-3 h-3 transition-transform ${expandedShipPeriods.has(p.key) ? 'rotate-90 text-teal-600' : ''}`} />
                            </td>
                            <td className="px-3 py-2 font-semibold text-slate-700">{p.label}</td>
                            <td className="px-3 py-2 text-center font-bold text-teal-600">{p.shipped_sets}</td>
                            <td className="px-3 py-2 text-center font-bold text-teal-700 bg-teal-50/30">{p.shipped_seat_placed.toLocaleString()}</td>
                            <td className="px-3 py-2 text-center font-bold text-emerald-600">{p.completed_sets}</td>
                            <td className="px-3 py-2 text-center font-bold text-violet-600 bg-violet-50/20">{p.completed_seat_placed.toLocaleString()}</td>
                          </tr>
                          {expandedShipPeriods.has(p.key) && p.details?.length > 0 && (
                            <tr>
                              <td colSpan={6} className="px-0 py-0">
                                <div className="bg-slate-50/70 border-y border-slate-200">
                                  <table className="w-full text-[10px]">
                                    <thead>
                                      <tr className="text-[8px] text-slate-400 uppercase">
                                        <th className="px-4 py-1 text-left">Project</th>
                                        <th className="px-3 py-1 text-left">Set Info</th>
                                        <th className="px-3 py-1 text-center">Shipped (set)</th>
                                        <th className="px-3 py-1 text-center text-teal-600">Shipped (sp)</th>
                                        <th className="px-3 py-1 text-center">Completed (set)</th>
                                        <th className="px-3 py-1 text-center text-violet-600">Completed (sp)</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {p.details.map((d, di) => (
                                        <tr key={di} className="border-t border-slate-100/80 hover:bg-white/60">
                                          <td className="px-4 py-1.5 font-semibold text-slate-600">{d.project}</td>
                                          <td className="px-3 py-1.5 text-slate-500">{d.set_info || '-'}</td>
                                          <td className="px-3 py-1.5 text-center font-bold text-teal-600">{d.shipped}</td>
                                          <td className="px-3 py-1.5 text-center font-bold text-teal-700">{d.seat_placed_shipped?.toLocaleString() || 0}</td>
                                          <td className="px-3 py-1.5 text-center font-bold text-emerald-600">{d.completed || d.shipped}</td>
                                          <td className="px-3 py-1.5 text-center font-bold text-violet-600">{d.seat_placed_completed?.toLocaleString() || d.seat_placed_shipped?.toLocaleString() || 0}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                      {/* Grand total row */}
                      <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold text-xs">
                        <td className="px-3 py-2"></td>
                        <td className="px-3 py-2 text-slate-700">TOTAL</td>
                        <td className="px-3 py-2 text-center text-teal-700">{shipmentData.totals.shipped_sets}</td>
                        <td className="px-3 py-2 text-center text-teal-800 bg-teal-50/30">{shipmentData.totals.shipped_seat_placed.toLocaleString()}</td>
                        <td className="px-3 py-2 text-center text-emerald-700">{shipmentData.totals.completed_sets}</td>
                        <td className="px-3 py-2 text-center text-violet-700 bg-violet-50/20">{shipmentData.totals.completed_seat_placed.toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Delivery Performance for this category - only on overview */}
      {!selectedProject && <DeliveryReport category={category} />}
    </div>
  );
}

export default CoachReport;
